import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { renameProject } from '../../apiService';
import { useProjectStore } from '../../stores/projectStore';
import { useToast } from '../ui/Toast';

interface Props {
  projectId: string;
  projectName: string;
  userEmail: string;
  onRenamed?: () => void;
}

export default function EditableProjectTitle({
  projectId,
  projectName,
  userEmail,
  onRenamed,
}: Props) {
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(projectName);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Guards against double-submit when Enter and blur fire together — without
  // this, hitting Enter while the input also loses focus sends two PATCH
  // requests. Mirrors the same guard in ProjectLibraryView's rename flow.
  const commitInFlightRef = useRef(false);

  useEffect(() => {
    if (isEditing) {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }
  }, [isEditing]);

  // Sync draft when the underlying project name changes from elsewhere (e.g.
  // another tab, another user) while we're not actively editing.
  useEffect(() => {
    if (!isEditing) setDraft(projectName);
  }, [projectName, isEditing]);

  const beginEdit = () => {
    setDraft(projectName);
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(projectName);
    setIsEditing(false);
  };

  const commit = async () => {
    if (commitInFlightRef.current) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === projectName) {
      cancel();
      return;
    }
    commitInFlightRef.current = true;
    setSubmitting(true);
    try {
      const result = await renameProject(projectId, userEmail, trimmed);
      if (result.success) {
        // Optimistically reflect the new name in the active-project slice so
        // the header (and anything else reading activeProject.name) updates
        // without waiting for the project-list refetch.
        const store = useProjectStore.getState();
        if (store.activeProject?.project_id === projectId) {
          useProjectStore.setState({
            activeProject: { ...store.activeProject, name: trimmed },
          });
        }
        setIsEditing(false);
        onRenamed?.();
      } else {
        showToast({
          variant: 'error',
          message: `Failed to rename project: ${result.error}`,
        });
      }
    } finally {
      setSubmitting(false);
      commitInFlightRef.current = false;
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        onBlur={commit}
        disabled={submitting}
        maxLength={200}
        aria-label="Project name"
        data-testid="workspace-project-rename-input"
        className="min-w-[200px] max-w-md px-2 py-0.5 text-base font-semibold bg-surface-sunken border border-border-default rounded-md text-fg focus:outline-none focus:border-accent focus:shadow-bark-focus transition-shadow disabled:opacity-60"
      />
    );
  }

  return (
    <span className="group inline-flex items-center gap-1.5 min-w-0">
      <h1
        onClick={beginEdit}
        title="Rename project"
        data-testid="workspace-project-title"
        className="text-base font-semibold text-fg truncate cursor-text hover:bg-bg-warm rounded px-1 -mx-1 transition-colors"
      >
        {projectName}
      </h1>
      <button
        type="button"
        onClick={beginEdit}
        aria-label="Rename project"
        title="Rename project"
        className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded
                   text-fg-tertiary opacity-60
                   hover:opacity-100 hover:bg-bg-warm
                   focus:opacity-100 focus:outline-none focus-visible:shadow-bark-focus
                   transition-opacity"
      >
        <Pencil className="w-3 h-3" strokeWidth={1.5} />
      </button>
    </span>
  );
}
